import React, { useEffect } from "react";
import Eyebrow from "@/components/Eyebrow";
import { FadeUp } from "@/components/Motion";
import PageHeader from "@/components/PageHeader";
import CTABlock from "@/components/CTABlock";

const musts = ["Tracked", "Documented", "Reconciled", "Defensible"];
const guarantees = ["Accurate", "Organized", "Fully compliant"];

const GHL_SCRIPT_SRC = "https://links.usabookkeepingsolutionsllc.com/js/form_embed.js";

const checklistItems = [
  "Cash position by account, on demand",
  "A reconciled trust balance, per matter",
  "Aged AR and unbilled time, clearly",
  "A clean monthly P&L with no surprises",
  "Operating vs. trust activity, separated",
  "Tax-ready categorization, every month",
  "Documentation that survives an audit",
];

export default function TrustAttorneys() {
  useEffect(() => {
    const existing = document.querySelector(`script[src="${GHL_SCRIPT_SRC}"]`);
    if (existing) return;
    const s = document.createElement("script");
    s.src = GHL_SCRIPT_SRC;
    s.async = true;
    document.body.appendChild(s);
  }, []);

  return (
    <div data-testid="trust-attorneys-page">
      <PageHeader
        eyebrow="Trust Attorneys"
        title={<>Complete <em className="text-[#BA1A1A] not-italic">Confidence</em> in Every Client Dollar.</>}
        lead="Your firm handles funds that don't belong to you. Every transaction must be tracked, documented, reconciled, and defensible."
        image={{
          src: "https://images.pexels.com/photos/12983110/pexels-photo-12983110.jpeg",
          alt: "Detailed vintage financial ledger",
        }}
        meta={[
          { label: "Fiduciary", value: "Duty" },
          { label: "Ledgers", value: "Per Client" },
          { label: "Posture", value: "Defensible" },
        ]}
      />

      <section className="container-editorial py-24 md:py-32" data-testid="trust-attorneys-body">
        <div className="grid md:grid-cols-12 gap-12">
          <div className="md:col-span-5">
            <Eyebrow>The Reality</Eyebrow>
            <h2 className="font-serif text-4xl md:text-5xl tracking-tight leading-[1.05] mt-6">
              Every transaction must be:
            </h2>
            <ul className="mt-10 space-y-5">
              {musts.map((m) => (
                <li key={m} className="flex items-center gap-4 font-serif text-2xl text-[#0C1D36]">
                  <span className="w-2 h-2 bg-[#BA1A1A] rounded-full" />
                  {m}
                </li>
              ))}
            </ul>
          </div>
          <div className="md:col-span-7 md:border-l md:border-[#0C1D36]/15 md:pl-12">
            <Eyebrow muted>We Ensure</Eyebrow>
            <p className="font-serif text-3xl md:text-4xl leading-tight text-[#0C1D36] mt-6">
              Your trust accounts are:
            </p>
            <div className="mt-8 grid sm:grid-cols-3 gap-px bg-[#0C1D36]/12">
              {guarantees.map((g) => (
                <FadeUp key={g} className="bg-white p-8">
                  <div className="eyebrow-muted">Guarantee</div>
                  <div className="font-serif text-2xl text-[#0C1D36] mt-3">{g}</div>
                </FadeUp>
              ))}
            </div>
            <p className="mt-10 font-serif italic text-xl text-[#0C1D36]/90 max-w-md">
              So you're never second-guessing your numbers.
            </p>
          </div>
        </div>
      </section>

      {/* LEAD MAGNET: Financial Clarity Checklist */}
      <section
        className="bg-[#F3EFE9] rule-top rule-bottom"
        data-testid="trust-checklist-section"
      >
        <div className="container-editorial py-24 md:py-32">
          <div className="grid md:grid-cols-12 gap-12 lg:gap-16 items-start">
            <div className="md:col-span-5">
              <Eyebrow>Free Download</Eyebrow>
              <h2 className="font-serif text-4xl md:text-5xl lg:text-6xl tracking-tight leading-[1.02] text-[#0C1D36] mt-6">
                The Financial Clarity Checklist.
              </h2>
              <p className="mt-6 font-serif italic text-xl md:text-2xl text-[#0C1D36]/90 max-w-md leading-snug">
                7 things your books must show every month, and what happens when they don't.
              </p>

              <ul className="mt-10 space-y-3 max-w-md">
                {checklistItems.map((item, i) => (
                  <li
                    key={item}
                    className="flex items-start gap-3 text-[15px] text-[#0C1D36]/85"
                  >
                    <span className="mt-2 w-1.5 h-1.5 bg-[#BA1A1A] rounded-full flex-shrink-0" />
                    <span>
                      <span className="font-mono text-xs text-[#4B5563] mr-2">
                        0{i + 1}
                      </span>
                      {item}
                    </span>
                  </li>
                ))}
              </ul>

              <p className="mt-10 text-sm text-[#4B5563] max-w-md">
                A two-minute self-audit for trust attorneys. Catch the gaps before a bar
                audit, lender, or partner does.
              </p>
            </div>

            <div className="md:col-span-7" data-testid="trust-checklist-form-wrapper">
              <div className="bg-white border border-[#0C1D36]/12 p-2 md:p-3 shadow-sm">
                <iframe
                  src="https://links.usabookkeepingsolutionsllc.com/widget/form/FrHCpAesMyPPVkANKJ4i"
                  style={{
                    width: "100%",
                    minHeight: "860px",
                    border: "none",
                    borderRadius: "8px",
                    background: "#FFFFFF",
                  }}
                  id="inline-FrHCpAesMyPPVkANKJ4i"
                  data-layout="{'id':'INLINE'}"
                  data-trigger-type="alwaysShow"
                  data-trigger-value=""
                  data-activation-type="alwaysActivated"
                  data-activation-value=""
                  data-deactivation-type="neverDeactivate"
                  data-deactivation-value=""
                  data-form-name="Trust Checklist"
                  data-height="829"
                  data-layout-iframe-id="inline-FrHCpAesMyPPVkANKJ4i"
                  data-form-id="FrHCpAesMyPPVkANKJ4i"
                  title="Trust Checklist"
                  data-testid="trust-checklist-iframe"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      <CTABlock
        eyebrow="Trust Attorneys"
        title="Should the books behind your fiduciary work be this clean?"
        ctaLabel="Request a Financial Review"
      />
    </div>
  );
}
